import { CSSProperties, memo, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { LocaleValuesFragment, RegionsEnum, VssValveAmFragment, VssValveEuFragment } from 'src/generated/operations';
import { MESSAGES } from 'src/modules/common/constants';
import { AttributesList, AttributesListItem } from 'src/utils/createAttributesItem.ts';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage.tsx';
import { preventDefault } from 'src/modules/common/utils/preventDefault.ts';
import { ConfigContext } from 'src/configContext.ts';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { PDFIcon } from 'src/modules/common/icons/PDFIcon.tsx';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon.tsx';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon.tsx';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { AttributesAliases } from 'src/generated/dict.ts';
import { createFileGaClickEvent } from 'src/utils/createFileGaClickEvent.ts';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import clsx from 'clsx';
import { composeValue } from 'src/common/composeValue.ts';
import { Button } from 'src/common/Button/Button';
import { ValveFlowWarning } from 'src/modules/valves/components/ValveFlowWarning.tsx';
import { DeviceViewType } from 'src/common/types/DeviceViewType';

const isGlobeValve = (
    product: VssValveEuFragment | VssValveAmFragment,
) => (product.family?.raw || []).join('').includes('Globe');

export type DeviceProps = {
    chip?: ReactNode;
    defaultImage: ReactNode;
    product: VssValveEuFragment | VssValveAmFragment;
    attributes: [AttributesList, AttributesList];
    selectButton?: ReactNode;
    links?: Array<LocaleValuesFragment | null | undefined>;
    price?: ReactNode;
    counter?: ReactNode;
    actions?: ReactNode;
    designData?: string;
    view: DeviceViewType;
    fullHeight?: boolean;
    collapsedAttributesPerColumn?: number;
    disableProductLink?: boolean;
}

export const Device = memo(({
    chip,
    defaultImage,
    product,
    attributes,
    selectButton,
    links = [],
    price,
    counter,
    actions,
    designData = '',
    view,
    fullHeight = false,
    collapsedAttributesPerColumn = 2,
    disableProductLink = false,
}: DeviceProps) => {
    const { userSettings, gaPush } = useContext(ConfigContext);
    const [parametersOpen, setParametersOpen] = useState(false);

    const isListCollapsed = view === 'list' && !parametersOpen;
    const isExpanded = view === 'grid' || parametersOpen;
    const showFooterDetails = view === 'default' || isExpanded || fullHeight;
    const showParametersToggle = !isListCollapsed && view !== 'grid' && !fullHeight;

    const composedAttributes = useMemo(
        () => {
            if (isExpanded || fullHeight) {
                return attributes;
            }

            const [firstColumn, secondColumn] = attributes;

            if (view === 'mobile') {
                const firstFiveAttributes = [...firstColumn, ...secondColumn].reduce((prev, curr) => {
                    if (prev.length < 4) {
                        return [...prev, curr];
                    }

                    return prev;
                }, [] as AttributesListItem[]);

                return [firstFiveAttributes];
            }

            if (view === 'list') {
                return [
                    ...firstColumn.slice(0, collapsedAttributesPerColumn).map((item) => [item]),
                    ...secondColumn.slice(0, collapsedAttributesPerColumn).map((item) => [item]),
                ];
            }

            return attributes;
        },
        [attributes, fullHeight, isExpanded, view, collapsedAttributesPerColumn],
    );

    const rowCount = Math.max(...composedAttributes.map((column) => column.length));
    const columnCount = isListCollapsed ? composedAttributes.length : 2;

    const { specification } = product;

    const onFileClick = useCallback((url: string) => {
        gaPush(createFileGaClickEvent(url));
    }, [gaPush]);

    return (
        <div
            data-testid="device"
            className="vss-device"
        >
            <div className="vss-device__header">
                <div className="vss-device__details">
                    <div className="vss-device__product">
                        {disableProductLink && product.Label}
                        {!disableProductLink && (
                            <ViewProductLink
                                userSettings={userSettings}
                                code={product.Label}
                                content={designData}
                            >
                                <span className="vss-device__product-label">
                                    {product.Label}
                                </span>

                                <div className="vss-device__product-icon">
                                    <ArrowRightIcon/>
                                </div>
                            </ViewProductLink>
                        )}

                        {view !== 'mobile' && chip && (
                            <div className="vss-device__recommendations">
                                {chip}
                            </div>
                        )}
                    </div>
                </div>

                <div className="vss-device__buttons">
                    {selectButton}
                </div>

            </div>

            <div className={clsx({
                'vss-device__body': true,
                'vss-device__body--list-collapsed': isListCollapsed,
            })}
            >
                <div className={clsx({
                    'vss-device__content': true,
                    'vss-device__content--mobile': view === 'mobile',
                    'vss-device__content--list-collapsed': isListCollapsed,
                })}
                >
                    <div className="vss-device__content-head">
                        <div className={clsx({
                            'vss-device__image': true,
                            'vss-device__image--hidden': isListCollapsed,
                        })}
                        >
                            <ProductImage
                                imgSrc={product.image?.raw.join('') || ''}
                            >
                                {defaultImage}
                            </ProductImage>
                        </div>

                        {view === 'mobile' && (
                            <div className="vss-device__recommendations">
                                {chip}
                            </div>
                        )}
                    </div>

                    <div
                        data-testid="device-parameters-wrapper"
                        className="vss-device__parameters-wrapper"
                        style={{
                            '--vss-device-row-count': rowCount,
                            '--vss-device-column-count': columnCount,
                        } as CSSProperties}
                    >
                        <div className={clsx({
                            'vss-device__parameters': true,
                            'vss-device__parameters--mobile': view === 'mobile',
                        })}
                        >
                            {composedAttributes.map((column, index) => (
                                <div
                                    className={clsx({
                                        'vss-device__parameters-column': true,
                                        'vss-device__parameters-column--mobile': view === 'mobile',
                                    })}
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                >
                                    {
                                        column.map(([key, value], i) => {
                                            const valueComposed = composeValue(value);
                                            const isFlowVelocityWarningAllowed = isGlobeValve(product) &&
                                                userSettings.currentRegion !== RegionsEnum.Am &&
                                                key === 'FlowVelocity';
                                            return (
                                                <div
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    key={`${key}_${i}`}
                                                    className={clsx({
                                                        'vss-device__parameter': true,
                                                        'vss-device__parameter--list-collapsed': isListCollapsed,
                                                    })}
                                                    role="row"
                                                    tabIndex={0}
                                                    onKeyDown={() => null}
                                                    onClick={preventDefault}
                                                >
                                                    <div className="vss-device__label">
                                                        <I18n messageId={key}/>
                                                    </div>
                                                    {isFlowVelocityWarningAllowed && (
                                                        <ValveFlowWarning value={valueComposed}/>
                                                    )}
                                                    {!isFlowVelocityWarningAllowed && (
                                                        <RenderHtml
                                                            className="vss-device__value"
                                                            data={valueComposed}
                                                        />
                                                    )}

                                                </div>
                                            );
                                        })
                                    }
                                </div>
                            ))}
                        </div>
                    </div>

                    {isListCollapsed && (
                        <div className="vss-device__parameters-actions">
                            <Button
                                size="lg"
                                kind="ghost-secondary"
                                iconOnly={true}
                                onClick={() => setParametersOpen((prev) => !prev)}
                            >
                                <div className="vss-device__parameters-toggle-icon">
                                    <ChevronDownIcon/>
                                </div>
                            </Button>
                        </div>
                    )}
                </div>

                {(showFooterDetails || actions) && (
                    <div className={clsx({
                        'vss-device__footer': true,
                        'vss-device__footer--list-collapsed': isListCollapsed,
                    })}
                    >
                        {price && showFooterDetails && (
                            <div className={clsx({
                                'vss-device__price-wrapper': true,
                                'vss-device__price-wrapper--mobile': view === 'mobile',
                            })}
                            >
                                <div className="vss-device__price">
                                    <div className="vss-device__price-label">
                                        <I18n messageId={MESSAGES.DevicePricePerUnit}/>
                                    </div>

                                    <div className="vss-device__price-value">
                                        {price}
                                    </div>
                                </div>

                                {counter}
                            </div>
                        )}

                        {showFooterDetails && (
                            <div className="vss-device__links">
                                {specification && (
                                    <a
                                        href={specification.localized.join('')}
                                        onClick={() => onFileClick(specification?.localized.join(''))}
                                        className="vss-link--secondary"
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <span className="vss-link__text">
                                            <I18n messageId={AttributesAliases.specification}/>
                                        </span>
                                        <span className="vss-link__icon">
                                            <PDFIcon/>
                                        </span>
                                    </a>
                                )}
                                {links && links
                                    .map((link) => {
                                        if (!link) {
                                            return '';
                                        }
                                        return (
                                            <a
                                                key={link.localized.join('')}
                                                href={link.localized.join('')}
                                                onClick={() => onFileClick(link?.localized.join(''))}
                                                className="vss-link--secondary"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                <span className="vss-link__text">
                                                    <I18n messageId={AttributesAliases.specification}/>
                                                </span>
                                                <span className="vss-link__icon">
                                                    <PDFIcon/>
                                                </span>
                                            </a>
                                        );
                                    })}
                            </div>
                        )}

                        {actions && (
                            <div className="vss-device__actions">
                                {actions}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {showParametersToggle && (
                <div className="vss-device__parameters-toggle">
                    <Button
                        size="lg"
                        kind="ghost-secondary"
                        iconOnly={true}
                        onClick={() => setParametersOpen((prev) => !prev)}
                    >
                        <div className="vss-device__parameters-toggle-icon">
                            {parametersOpen ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                        </div>
                    </Button>
                </div>
            )}
        </div>
    );
});
